import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from '../../../utils/classNames';
import './index.scss';

const HeartIcon = ({ filled }) => (
  <svg
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 21s-7-4.5-9.5-9.5C.7 7.7 3.4 4 7 4c2 0 3.7 1.1 5 3 1.3-1.9 3-3 5-3 3.6 0 6.3 3.7 4.5 7.5C19 16.5 12 21 12 21z" />
  </svg>
);

HeartIcon.propTypes = { filled: PropTypes.bool };
HeartIcon.defaultProps = { filled: false };

/**
 * Reusable Like button. Same visual pattern as comment likes — heart icon
 * that fills when liked, with a "Like" / "Liked" text label and an optional
 * count after. Includes a small bump animation on click for feedback.
 */
export default function LikeButton({
  liked = false,
  count,
  disabled = false,
  onToggle,
  likeLabel = 'Like',
  likedLabel = 'Liked',
  showCount = true,
  className = '',
}) {
  const [bump, setBump] = useState(0);
  const previousLikedRef = useRef(liked);

  // Replay the heart bump whenever the liked state flips, so the animation
  // also fires for parent-driven changes (e.g. reactions refreshed from server).
  if (previousLikedRef.current !== liked) {
    previousLikedRef.current = liked;
    // setState inside render is fine when guarded by ref comparison.
    setBump((value) => value + 1); // eslint-disable-line react-hooks/rules-of-hooks
  }

  const handleClick = () => {
    if (disabled || !onToggle) return;
    // Bump immediately on click for snappy feedback, ahead of any server
    // round-trip — mirrors the feed-page comment-like behaviour.
    setBump((value) => value + 1);
    onToggle(!liked);
  };

  const classes = classNames('like-button', className, {
    'like-button--liked': liked,
    'like-button--disabled': disabled,
  });

  return (
    <button
      type="button"
      className={classes}
      onClick={handleClick}
      disabled={disabled}
      aria-pressed={liked}
    >
      <span
        className="like-button__icon"
        key={bump}
        data-bump={bump > 0 ? 'true' : undefined}
      >
        <HeartIcon filled={liked} />
      </span>
      <span className="like-button__label">
        {liked ? likedLabel : likeLabel}
      </span>
      {showCount && typeof count === 'number' && count > 0 ? (
        <span className="like-button__count">{count}</span>
      ) : null}
    </button>
  );
}

LikeButton.propTypes = {
  liked: PropTypes.bool,
  count: PropTypes.number,
  disabled: PropTypes.bool,
  onToggle: PropTypes.func,
  likeLabel: PropTypes.node,
  likedLabel: PropTypes.node,
  showCount: PropTypes.bool,
  className: PropTypes.string,
};
